import React, { useCallback } from 'react';
import { ClickAwayListener, Paper, PopperPlacementType, Stack } from '@mui/material';
import { Popper } from '@mui/material';
import { abyssColors } from '@/theme/colors';
import { FilterPills } from './FilterPills';
import type { FilterPill } from './InsightsSearchBar.types';

interface OverflowChipsMenuProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  hiddenPills: FilterPill[];
  onClose: () => void;
  onRemovePill: (pill: FilterPill) => void;
  onEditPill?: (pill: FilterPill) => void;
  editingPill?: FilterPill | null;
  editingValue?: string;
  onChangeEditingValue?: (value: string) => void;
  onSaveEditedPill?: (pill: FilterPill, newValue: string) => void;
  onCancelEdit?: () => void;
}

export const OverflowChipsMenu = ({
  open,
  anchorEl,
  hiddenPills,
  onClose,
  onRemovePill,
  onEditPill,
  editingPill,
  editingValue,
  onChangeEditingValue,
  onSaveEditedPill,
  onCancelEdit,
}: OverflowChipsMenuProps): React.ReactElement | null => {
  const handleRemovePill = useCallback(
    (pill: FilterPill): void => {
      onRemovePill(pill);
    },
    [onRemovePill],
  );

  const handleEditPill = useCallback(
    (pill: FilterPill): void => {
      onEditPill?.(pill);
      onClose();
    },
    [onEditPill, onClose],
  );

  const handleSaveEditedPill = useCallback(
    (pill: FilterPill, newValue: string): void => {
      onSaveEditedPill?.(pill, newValue);
      onClose();
    },
    [onSaveEditedPill, onClose],
  );

  if (!open || hiddenPills.length === 0) return null;

  return (
    <ClickAwayListener onClickAway={onClose}>
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement={"bottom-start" as PopperPlacementType}
        style={{ zIndex: 1_300 }}
      >
        <Paper
          sx={{
            p: 1.25,
            border: `1px solid ${abyssColors.primary[200]}`,
            borderRadius: '4px',
            boxShadow: '0px 4px 12px rgba(0,0,0,0.15)',
            maxWidth: '30rem',
          }}
        >
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            <FilterPills
              pills={hiddenPills}
              onRemovePill={handleRemovePill}
              onEditPill={handleEditPill}
              editingPill={editingPill}
              editingValue={editingValue}
              onChangeEditingValue={onChangeEditingValue}
              onSaveEditedPill={handleSaveEditedPill}
              onCancelEdit={onCancelEdit}
            />
          </Stack>
        </Paper>
      </Popper>
    </ClickAwayListener>
  );
};
